/**
 * Back-compat surface for budget calculations. Per-model logic lives in
 * src/lib/models/*. This file:
 *   - Provides `calculateDailyAllowance(data, today)` so existing
 *     consumers (TodayScreen, achievements, BudgetHeatmap) keep working.
 *   - Re-exports model functions with the legacy `(data, today)`
 *     signature that computes the period internally.
 *   - Re-exports shared helpers (txContributionInRange,
 *     effectiveSpreadEnd, etc.) for code that imported them from here.
 *
 * Anything new should reach into ./models/* directly.
 */

import type { AppData } from '../data/types';
import { startOfDay } from './date';
import { getPayPeriod, type PayPeriod } from './payPeriod';
import {
  AVG_DAYS_PER_MONTH,
  resolveWorkDaysPerWeek,
  roundMoney,
  sumExpensesInRange,
  sumIncomeInRange,
  sumScheduledByType,
  workdaysInRange,
} from './models/shared';
import {
  calculate503020 as _calculate503020,
  calculateBudget,
  calculateDaily as _calculateDaily,
  calculatePYF as _calculatePYF,
  calculateZero as _calculateZero,
} from './models';

export type {
  BucketResult,
  CategoryAllocation,
  DailyModelResult,
  DailyStatus,
  FiftyThirtyTwentyResult,
  PayYourselfFirstResult,
  ZeroBasedResult,
} from './models';

export {
  effectiveSpreadEnd,
  resolvePeriodIncome,
  computeRolloverAmount,
  txContributionInRange,
  txContributionOnDate,
} from './models/shared';

export interface DailyAllowanceResult {
  period: PayPeriod;
  /** This period's income from one-off + recurring sources. */
  periodIncome: number;
  /** Carried-over surplus/deficit from prior periods. 0 when rollover='fresh'. */
  rolloverAmount: number;
  /** Period pool after PYF + scheduled bills (zero-based: net of allocations). */
  discretionaryBudget: number;
  /** Daily income rate for display: periodIncome / totalDays. */
  dailyBudget: number;
  /** Sum of expense-transaction contributions in [period.start, today]. */
  spentThisPeriod: number;
  /** Sum of expense-transaction contributions dated today. */
  spentToday: number;
  /** Running balance through end-of-today. */
  availableToday: number;
  /** Spendable cash left: periodIncome − spentThisPeriod − pyfToDate. */
  remainingThisPeriod: number;
  status: import('./models').DailyStatus;
}

function pyfPeriodReserve(data: AppData, period: PayPeriod): number {
  if (data.profile.budgetModel !== 'pyf') return 0;
  const hourlyRate = data.profile.hourlyRate ?? 0;
  if (hourlyRate <= 0) return 0;
  const wdpw = resolveWorkDaysPerWeek(data.profile.workDaysPerWeek);
  return roundMoney(
    hourlyRate * workdaysInRange(period.start, period.end, wdpw),
  );
}

/**
 * Period framing pool used by achievements ("did last period's spending
 * fit?"). Per-model:
 *   - zero-based: reserves the sum of category allocations.
 *   - everything else: reserves only the PYF auto-save. Scheduled bills
 *     are NOT pre-reserved here — they auto-deduct on their due date
 *     (via sumSpendingWithScheduledInRange) and are already counted in
 *     spentThisPeriod, so subtracting them again upfront would double-
 *     count against the achievement.
 */
function computeTotalReserved(data: AppData, period: PayPeriod): number {
  if (data.profile.budgetModel === 'zero') {
    const monthlyAllocated = data.categories
      .filter((c) => c.type === 'expense')
      .reduce((sum, c) => sum + (c.zeroBasedBudget ?? 0), 0);
    return roundMoney(
      monthlyAllocated * (period.totalDays / AVG_DAYS_PER_MONTH),
    );
  }
  return pyfPeriodReserve(data, period);
}

/**
 * Back-compat entry point. Adapts the dispatched model result into the
 * legacy DailyAllowanceResult shape. New code should call
 * `calculateBudget` (./models) directly.
 */
export function calculateDailyAllowance(
  data: AppData,
  today: Date = new Date(),
): DailyAllowanceResult {
  const t = startOfDay(today);
  const period = getPayPeriod(data.profile, t);
  const r = calculateBudget(data, period, t);

  const totalReserved = computeTotalReserved(data, period);
  const discretionaryBudget = roundMoney(
    r.periodIncome + r.rolloverAmount - totalReserved,
  );

  return {
    period,
    periodIncome: r.periodIncome,
    rolloverAmount: r.rolloverAmount,
    discretionaryBudget,
    dailyBudget: r.dailyIncomeRate,
    spentThisPeriod: r.spentThisPeriod,
    spentToday: r.spentToday,
    availableToday: r.availableToday,
    remainingThisPeriod: r.remainingThisPeriod,
    status: r.status,
  };
}

/** Period-scoped wrappers preserving the legacy `(data, today)` signature. */
export function calculatePYF(data: AppData, today: Date = new Date()) {
  const t = startOfDay(today);
  const period = getPayPeriod(data.profile, t);
  return _calculatePYF(data, period, t);
}

export function calculate503020(data: AppData, today: Date = new Date()) {
  const t = startOfDay(today);
  const period = getPayPeriod(data.profile, t);
  return _calculate503020(data, period, t);
}

export function calculateZeroBased(data: AppData, today: Date = new Date()) {
  const t = startOfDay(today);
  const period = getPayPeriod(data.profile, t);
  return _calculateZero(data, period, t);
}

export function calculateDaily(data: AppData, today: Date = new Date()) {
  const t = startOfDay(today);
  const period = getPayPeriod(data.profile, t);
  return _calculateDaily(data, period, t);
}

export interface PeriodSummary {
  period: PayPeriod;
  income: number;
  expenses: number;
  net: number;
  scheduledIncome: number;
  scheduledExpenses: number;
}

export function getPeriodSummary(
  data: AppData,
  today: Date = new Date(),
): PeriodSummary {
  const period = getPayPeriod(data.profile, today);
  const income = sumIncomeInRange(data.transactions, period.start, period.end);
  const expenses = sumExpensesInRange(
    data.transactions,
    period.start,
    period.end,
  );
  return {
    period,
    income,
    expenses,
    net: roundMoney(income - expenses),
    scheduledIncome: sumScheduledByType(data.scheduledItems, period, 'income'),
    scheduledExpenses: sumScheduledByType(
      data.scheduledItems,
      period,
      'expense',
    ),
  };
}
